import React, { useMemo, useState } from 'react';
import {
  useFloating,
  flip,
  offset,
  Middleware,
  autoUpdate,
  autoPlacement,
  FloatingContext,
  ReferenceType,
  shift,
  Padding,
} from '@floating-ui/react-dom-interactions';

export const isEnabled = (
  value: null | undefined | false | { enabled?: boolean },
  defaultValue: boolean,
) => {
  if (value) {
    return value.enabled ?? defaultValue;
  }
  return value !== null && value !== false && defaultValue;
};

export type Placement =
  | 'top'
  | 'bottom'
  | 'right'
  | 'left'
  | 'top-start'
  | 'top-end'
  | 'bottom-start'
  | 'bottom-end'
  | 'right-start'
  | 'right-end'
  | 'left-start'
  | 'left-end';

export type Alignment = 'start' | 'end';

/**
 * Changes the placement of the floating element to the opposite one by default in order to keep it in view. Default: Enabled
 * * fallbackPlacements - This describes the array of placements to try if the preferred placement doesn't fully fit on the axes in which overflow is checked. Default: [oppositePlacement]
 */
interface FlipConfig {
  enabled: boolean;
  fallbackPlacements?: Placement[];
  padding?: Padding;
}

const DefaultFlipConfig: FlipConfig = {
  enabled: true,
  fallbackPlacements: undefined,
  padding: undefined,
};

const DefaultOffsetConfig = {
  enabled: true,
  offset: [6, 0] as [number, number],
};
/**
 *  Offset configuration: Default: enabled
 *  * offset - [mainAxis, crossAxis]: Default: [6, 0]
 *    * mainAxis - Represents the distance (gutter or margin) between the floating element and the reference element.
 *    * crossAxis - Represents the skidding between the floating element and the reference element.
 *
 *  Examples:
 *  [6, 0] (Placement: right) - Position offset by 6 pixels to right from the position
 *  [-6, 0] (Placement: right) - Position offset by 6 pixels to left from the position
 *  [0, 6] (Placement: right) - Position offset by 5 pixels to bottom from the position
 */
type OffsetConfig = Partial<typeof DefaultOffsetConfig>;

/**
 *  Chooses the placement that has the most space available automatically. Default: disabled
 *  * alignment - By specifying an alignment, it will choose those aligned placements.
 */
type AutoConfig = {
  enabled?: boolean;
  alignment?: Alignment | null;
};

export type Options = {
  // Position of the element to the trigger element. Default: bottom-start
  placement: Placement;
  // Automatic position based on the space around the trigger. This overrides flip and placement options. Default: disabled
  auto: AutoConfig | null | false;
  // Flip position if there is not enough space on the placement area: Default: Enabled
  flip: FlipConfig | null | false;
  // Offset from the trigger element: Default: Enabled
  offset: OffsetConfig | null | false;
  // A visibility optimizer that shifts the floating element along the specified axes in order to keep it in view: Default: disabled
  shift: Pick<AutoConfig, 'enabled'> | null | false;
};

export type AutoPosition<T extends HTMLElement, Y extends HTMLElement> = {
  _context: FloatingContext<ReferenceType>;
  _open: boolean;
  triggerRef: React.RefCallback<T>;
  floatRef: React.RefCallback<Y>;
  styles: React.CSSProperties;
};

/**
 * Calculate position of "floating" elements like tooltips, popovers, dropdowns, menus, and more.
 * @param config Options to modify the behavior
 */
export const useAutoPosition = <
  T extends HTMLElement = HTMLElement,
  Y extends HTMLElement = HTMLElement,
>(
  config: Partial<Options> = {},
): AutoPosition<T, Y> => {
  const [open, setOpen] = useState(false);
  const options: Options = {
    placement: config.placement || 'bottom-start',
    auto: isEnabled(config.auto, false) && {
      ...config.auto,
    },
    flip: isEnabled(config.flip, true) && {
      ...DefaultFlipConfig,
      ...config.flip,
    },
    offset: isEnabled(config.offset, true) && {
      ...DefaultOffsetConfig,
      ...config.offset,
    },
    shift: isEnabled(config.shift, false) && {
      ...config.shift,
    },
  };
  const middleware: Middleware[] = [];
  if (options.flip && !options.auto) {
    middleware.push(
      flip({
        fallbackPlacements: options.flip.fallbackPlacements,
        padding: options.flip.padding,
      }),
    );
  }
  if (options.auto) {
    middleware.push(
      autoPlacement({
        alignment: options.auto.alignment,
      }),
    );
  }
  if (options.offset) {
    const [mainAxis, crossAxis] =
      options.offset.offset || DefaultOffsetConfig.offset;
    middleware.push(
      offset({
        mainAxis,
        crossAxis,
      }),
    );
  }
  if (options.shift) {
    middleware.push(shift());
  }

  const { x, y, reference, floating, strategy, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: options.placement,
    middleware,
    whileElementsMounted: autoUpdate,
  });

  const styles = useMemo<React.CSSProperties>(
    () => ({
      position: strategy,
      left: x ?? 'auto',
      top: y ?? 'auto',
    }),
    [x, y, strategy],
  );

  return {
    _context: context,
    _open: open,
    triggerRef: reference,
    floatRef: floating,
    styles,
  };
};

export default useAutoPosition;
